import React, { useState } from 'react';
import { useAdminTheme } from '../context/AdminThemeContext';
import { UserPlus, Upload, FileSpreadsheet, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import Notification from '../../Component/Notification'; // Adjust path

const AddStudent: React.FC = () => {
  const { colors } = useAdminTheme();
  const [activeTab, setActiveTab] = useState<'single' | 'bulk'>('single');
  const [loading, setLoading] = useState(false);
  
  // Notification State
  const [notify, setNotify] = useState<{ isOpen: boolean; message: string; type: 'success' | 'error' }>({
    isOpen: false, message: '', type: 'success'
  });

  const [formData, setFormData] = useState({ name: '', email: '', mobile: '' });

  const handleSingleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // const response = await fetch('http://localhost:2025/api/admin/students', {
      const response = await fetch('https://www.projectinfotech.in/api/admin/students', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.text(); 

      if (response.ok) {
        setNotify({ isOpen: true, message: 'User added successfully!', type: 'success' });
        setFormData({ name: '', email: '', mobile: '' }); // Clear form
      } else {
        setNotify({ isOpen: true, message: data || 'Failed to add user', type: 'error' });
      }
    } catch (error) {
      setNotify({ isOpen: true, message: 'Server Connection Error', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const formData = new FormData();
      formData.append('file', file);
      
      setLoading(true);
      try {
        // const response = await fetch('http://localhost:2025/api/admin/students/bulk', {
        const response = await fetch('https://www.projectinfotech.in/api/admin/students/bulk', {
          method: 'POST',
          body: formData,
        });
        const data = await response.text();
        
        if(response.ok) {
            setNotify({ isOpen: true, message: 'Bulk upload processed successfully!', type: 'success' });
        } else {
            setNotify({ isOpen: true, message: data, type: 'error' });
        }
      } catch(err) {
        setNotify({ isOpen: true, message: 'Bulk upload failed.', type: 'error' });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="animate-fade-in-up max-w-4xl mx-auto">
      {/* Popup Notification */}
      <Notification 
        isOpen={notify.isOpen} 
        message={notify.message} 
        type={notify.type} 
        onClose={() => setNotify({ ...notify, isOpen: false })} 
      />

      <h2 className={`text-2xl font-bold mb-6 ${colors.navbarText}`}>Add New User</h2>

      <div className="flex gap-4 mb-6">
        <button
          onClick={() => setActiveTab('single')}
          className={`px-6 py-2 rounded-lg font-bold transition-all ${activeTab === 'single' ? colors.button3D + ' text-white shadow-lg' : `${colors.cardBg} ${colors.cardText} border ${colors.borderColor}`}`}
        >
          Single Entry
        </button>
        <button
          onClick={() => setActiveTab('bulk')}
          className={`px-6 py-2 rounded-lg font-bold transition-all ${activeTab === 'bulk' ? colors.button3D + ' text-white shadow-lg' : `${colors.cardBg} ${colors.cardText} border ${colors.borderColor}`}`}
        >
          Bulk Upload
        </button>
      </div>

      <div className={`p-8 rounded-xl shadow-sm border ${colors.cardBg} ${colors.cardText} ${colors.borderColor}`}>
        
        {activeTab === 'single' ? (
          <motion.form 
            initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
            onSubmit={handleSingleSubmit} className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold mb-2 opacity-80">Full Name</label>
                <input 
                  type="text" required
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  className={`w-full p-3 rounded-lg border focus:ring-2 focus:outline-none transition-all ${colors.inputBg} ${colors.borderColor}`}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 opacity-80">Email Address</label>
                <input 
                  type="email" required
                  value={formData.email}
                  onChange={e => setFormData({...formData, email: e.target.value})}
                  className={`w-full p-3 rounded-lg border focus:ring-2 focus:outline-none transition-all ${colors.inputBg} ${colors.borderColor}`}
                  placeholder="user@example.com"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold mb-2 opacity-80">Mobile Number</label>
                <input 
                  type="tel" required
                  value={formData.mobile}
                  onChange={e => setFormData({...formData, mobile: e.target.value})}
                  className={`w-full p-3 rounded-lg border focus:ring-2 focus:outline-none transition-all ${colors.inputBg} ${colors.borderColor}`}
                  placeholder="+91 9876543210"
                />
              </div>
            </div>
            
            <button type="submit" disabled={loading} className={`w-full md:w-auto px-8 py-3 rounded-lg font-bold text-white transition-transform active:scale-95 ${colors.button3D} flex items-center justify-center gap-2`}>
              {loading ? <Loader2 className="animate-spin" /> : <UserPlus size={20} />} Add User
            </button>
          </motion.form>
        ) : (
          <motion.div 
            initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
            className="text-center py-10"
          >
            <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 bg-green-100 text-green-600`}>
              <FileSpreadsheet size={40} />
            </div>
            <h3 className="text-xl font-bold mb-2">Upload User Data</h3>
            <p className="opacity-60 mb-6 max-w-md mx-auto">
              Please upload an .xlsx or .csv file. The file should contain columns: <strong>Name, Email, Mobile</strong>.
            </p>
            
            <div className="relative inline-block">
              <input 
                type="file" 
                accept=".xlsx, .csv" 
                onChange={handleBulkUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <button disabled={loading} className={`px-8 py-3 rounded-lg font-bold text-white transition-transform active:scale-95 ${colors.button3D} flex items-center gap-2`}>
                 {loading ? <Loader2 className="animate-spin" /> : <Upload size={20} />} Select File
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AddStudent;